import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCollectPayment } from '../../hooks/useCollectPayment';
import { TranslationKey } from '../../i18n/translationKeys';
import { useToastStore } from '../../stores/toastStore';
import type { RelanceAgendaEntry } from '../../types/customerFollowUp/relanceAgenda';
import { Button } from '../../ui/Button/Button';
import { LinkButton } from '../../ui/LinkButton/LinkButton';
import { ModalShell } from '../../ui/ModalShell/ModalShell';
import { collectableRelance } from './collectableRelance';

interface ExpectedRelanceModalProps {
    entry: RelanceAgendaEntry;
    onClose: () => void;
}

/** An expected relance cannot be brought forward or ignored yet; it can only be collected. */
export const ExpectedRelanceModal: React.FC<ExpectedRelanceModalProps> = ({ entry, onClose }) => {
    const { t } = useTranslation();
    const showToast = useToastStore(s => s.showToast);
    const collectPayment = useCollectPayment();
    const { item } = entry;

    const handleCollect = () => {
        onClose();
        if (!collectPayment(collectableRelance(entry))) showToast('error', t(TranslationKey.Home.PaidAction.UnreachableError));
    };

    return (
        <ModalShell
            heading={t(TranslationKey.Home.ActionModal.HeadingExpected)}
            onClose={onClose}
            dismissAriaLabel={t(TranslationKey.Home.ActionModal.Close)}
            rootTestId={`expected-relance-modal-${item.id}`}
            footer={
                <Button variant="secondary" type="button" onClick={onClose} data-testid={`expected-relance-cancel-${item.id}`}>
                    {t(TranslationKey.Home.ReactivateModal.Cancel)}
                </Button>
            }
        >
            <p data-testid={`expected-relance-customer-${item.id}`} className="-mt-m text-s text-content-secondary">
                {item.title}{item.amount ? ` — ${item.amount}` : ''}
            </p>
            <div className="flex flex-col items-start gap-s">
                <LinkButton variant="navigation" weight="medium" chevron="end" onClick={handleCollect} data-testid={`expected-relance-collect-${item.id}`}>
                    {t(TranslationKey.Home.ActionModal.CollectBalance)}
                </LinkButton>
            </div>
        </ModalShell>
    );
};
